import { useState, useEffect } from "react";
import { handleExternalNav } from "@/lib/utils";
import { Layout } from "@/pages/Dashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClearableInput } from "@/components/ui/clearable-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Globe, Loader2, Check, X, ExternalLink, RefreshCw, Edit2, Save } from "lucide-react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

export const GigEnrichment = () => {
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGig, setSelectedGig] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [customUrl, setCustomUrl] = useState("");
  const [enrichResults, setEnrichResults] = useState(null);
  const [confirming, setConfirming] = useState(false);
  
  // Editable fields for confirmation
  const [editableData, setEditableData] = useState({});
  const [selectedFields, setSelectedFields] = useState({});

  useEffect(() => {
    fetchGigs();
  }, []);

  const fetchGigs = async () => {
    try {
      // Only fetch upcoming gigs - past gigs don't need enrichment
      const response = await apiClient.get("/gigs?time_filter=upcoming");
      setGigs(response.data);
    } catch (error) {
      toast.error("Failed to fetch gigs");
    } finally {
      setLoading(false);
    }
  };

  const handleEnrich = async () => {
    if (!selectedGig) {
      toast.error("Please select a gig first");
      return;
    }

    setEnriching(true);
    setEnrichResults(null);
    setEditableData({});
    setSelectedFields({});

    try {
      // First, do a preview search (don't save yet)
      const response = await apiClient.post("/gigs/enrich", {
        gig_id: selectedGig,
        search_query: customUrl || undefined,
        preview_only: true  // Preview mode - don't save
      });

      setEnrichResults(response.data);

      if (response.data.success && response.data.enrichment_data) {
        // Set up editable data and pre-select all found fields
        const data = response.data.enrichment_data;
        setEditableData(data);
        const fields = {};
        Object.keys(data).forEach(key => {
          fields[key] = true;  // Pre-select all fields
        });
        setSelectedFields(fields);
        toast.success("Found gig details! Review and confirm below.");
      } else if (response.data.found_data && Object.keys(response.data.found_data).length > 0) {
        // Even if no enrichment (all fields exist), show what was found
        toast.info("Search complete. Review any found details below.");
      } else {
        toast.info("Couldn't find additional details for this gig");
      }
    } catch (error) {
      toast.error("Enrichment failed. Try providing a direct venue URL.");
    } finally {
      setEnriching(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedGig || !enrichResults) return;

    // Build the data to save based on selected fields
    const dataToSave = {};
    Object.entries(selectedFields).forEach(([field, isSelected]) => {
      if (isSelected && editableData[field]) {
        dataToSave[field] = editableData[field];
      }
    });

    if (Object.keys(dataToSave).length === 0) {
      toast.error("Please select at least one field to save");
      return;
    }

    setConfirming(true);

    try {
      const response = await apiClient.post("/gigs/enrich/confirm", {
        gig_id: selectedGig,
        enrichment_data: dataToSave
      });

      if (response.data.success) {
        toast.success(`Updated ${response.data.updated_fields.length} fields!`);
        fetchGigs();
        setEnrichResults(null);
        setEditableData({});
        setSelectedFields({});
      } else {
        toast.error(response.data.message || "Failed to update gig");
      }
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setConfirming(false);
    }
  };

  const selectedGigData = gigs.find(g => g.gig_id === selectedGig);

  const getMissingFields = (gig) => {
    const missing = [];
    if (!gig.ticket_link) missing.push("ticket_link");
    if (!gig.ticket_price) missing.push("ticket_price");
    if (!gig.start_time) missing.push("start_time");
    if (!gig.venue_address) missing.push("venue_address");
    return missing;
  };

  const formatFieldName = (field) => {
    return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Layout>
      <div data-testid="enrich-page">
        {/* Header */}
        <div className="mb-8">
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-2">Gig Enrichment</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Find Venue Details</h1>
          <p className="text-muted-foreground mt-2">
            Automatically search for ticket links, prices, and times. Review and confirm before saving.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gig Selection */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">Select a Gig</CardTitle>
              <CardDescription>
                Choose a gig to enrich with venue details
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-secondary/50 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : gigs.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No gigs found. Add some gigs first!
                </p>
              ) : (
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-2">
                    {gigs.map(gig => {
                      const missing = getMissingFields(gig);
                      const isSelected = selectedGig === gig.gig_id;
                      
                      return (
                        <div
                          key={gig.gig_id}
                          className={`p-4 rounded-lg cursor-pointer transition-all ${
                            isSelected 
                              ? 'bg-primary/20 border border-primary' 
                              : 'bg-secondary/30 hover:bg-secondary/50 border border-transparent'
                          }`}
                          onClick={() => {
                            setSelectedGig(gig.gig_id);
                            setEnrichResults(null);
                            setEditableData({});
                            setSelectedFields({});
                            setCustomUrl(gig.venue_website || "");
                          }}
                          data-testid={`enrich-gig-${gig.gig_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{gig.artist_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {gig.venue_name}, {gig.city}
                              </p>
                              <p className="text-xs text-muted-foreground font-mono mt-1">
                                {gig.date ? format(parseISO(gig.date), 'MMM d, yyyy') : 'No date'}
                              </p>
                            </div>
                            {isSelected && (
                              <Check className="h-5 w-5 text-primary" />
                            )}
                          </div>
                          
                          {missing.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {missing.map(field => (
                                <Badge 
                                  key={field} 
                                  variant="outline" 
                                  className="text-xs text-amber-400 border-amber-500/30"
                                >
                                  Missing: {formatFieldName(field)}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Enrichment Panel */}
          <div className="space-y-6">
            {/* Search Options */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Smart Search</CardTitle>
                <CardDescription>
                  Searches ticket platforms with flexible artist matching
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="custom_url">Direct Ticket URL (optional)</Label>
                  <div className="flex gap-2">
                    <ClearableInput
                      id="custom_url"
                      value={customUrl}
                      onChange={(e) => setCustomUrl(e.target.value)}
                      placeholder="https://www.ticketsource.co.uk/..."
                      className="flex-1"
                      data-testid="venue-url-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Paste a direct ticket link for best results, or leave empty to auto-search.
                  </p>
                </div>

                {/* Platform badges */}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-xs">TicketSource</Badge>
                  <Badge variant="outline" className="text-xs">Eventbrite</Badge>
                  <Badge variant="outline" className="text-xs">StubHub</Badge>
                  <Badge variant="outline" className="text-xs">Ents24</Badge>
                  <Badge variant="outline" className="text-xs">Dice</Badge>
                  <Badge variant="outline" className="text-xs">WeGotTickets</Badge>
                </div>

                <Button
                  onClick={handleEnrich}
                  disabled={!selectedGig || enriching}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
                  data-testid="enrich-btn"
                >
                  {enriching ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Find Details
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Selected Gig Details */}
            {selectedGigData && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg">Current Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Artist</span>
                      <span className="text-sm font-medium">{selectedGigData.artist_name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Venue</span>
                      <span className="text-sm font-medium">{selectedGigData.venue_name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">City</span>
                      <span className="text-sm font-medium">{selectedGigData.city}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Start Time</span>
                      <span className="text-sm font-mono">
                        {selectedGigData.start_time || <span className="text-amber-400">Missing</span>}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Ticket Price</span>
                      <span className="text-sm font-mono">
                        {selectedGigData.ticket_price || <span className="text-amber-400">Missing</span>}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Ticket Link</span>
                      {selectedGigData.ticket_link ? (
                        <a
                          href={selectedGigData.ticket_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleExternalNav}
                          className="text-sm text-primary hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View
                        </a>
                      ) : (
                        <span className="text-sm text-amber-400">Missing</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Confirm Details Card - Shows when data is found */}
            {enrichResults && enrichResults.enrichment_data && Object.keys(enrichResults.enrichment_data).length > 0 && (
              <Card className="border border-emerald-500/50 bg-emerald-500/5">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Edit2 className="h-5 w-5 text-emerald-500" />
                    Confirm Details
                  </CardTitle>
                  <CardDescription>
                    Review and edit the found details before saving
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(editableData).map(([field, value]) => (
                    <div key={field} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`check-${field}`}
                          checked={selectedFields[field] || false}
                          onCheckedChange={(checked) => 
                            setSelectedFields(prev => ({...prev, [field]: checked}))
                          }
                          data-testid={`check-${field}`}
                        />
                        <Label htmlFor={`check-${field}`} className="text-sm font-medium">
                          {formatFieldName(field)}
                        </Label>
                      </div>
                      <Input
                        value={value || ""}
                        onChange={(e) => setEditableData(prev => ({...prev, [field]: e.target.value}))}
                        disabled={!selectedFields[field]}
                        className={!selectedFields[field] ? "opacity-50" : ""}
                        data-testid={`input-${field}`}
                      />
                    </div>
                  ))}

                  <Button
                    onClick={handleConfirm}
                    disabled={confirming || Object.values(selectedFields).every(v => !v)}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="confirm-enrich-btn"
                  >
                    {confirming ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Selected Details
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* No Results Card */}
            {enrichResults && (!enrichResults.enrichment_data || Object.keys(enrichResults.enrichment_data).length === 0) && (
              <Card className="border-border bg-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <X className="h-5 w-5 text-muted-foreground" />
                    No New Details Found
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      {enrichResults.found_data && Object.keys(enrichResults.found_data).length > 0
                        ? "Found some data, but the gig already has these fields filled in."
                        : "Couldn't find matching details. Try:"}
                    </p>
                    <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                      <li>Providing a direct venue website URL</li>
                      <li>Checking if the gig is listed online yet</li>
                      <li>Trying different artist name variations</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Source URLs - Always show when available */}
            {enrichResults && enrichResults.source_urls && enrichResults.source_urls.length > 0 && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-sm">Sources Found</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {enrichResults.source_urls.slice(0, 5).map((source, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Globe className="h-3 w-3 mt-1 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleExternalNav}
                            className="text-xs text-primary hover:underline truncate block"
                          >
                            {source.title || source.url}
                          </a>
                          {source.snippet && (
                            <p className="text-xs text-muted-foreground truncate">{source.snippet}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default GigEnrichment;
